/**
 * PROJECT-OVERVIEW §7.1 — Oportunidade.
 *
 * Manual entry and "promote from Discovery" land on the **same schema**; `origem`
 * is the only thing that distinguishes them.
 *
 * **All five value fields are monthly.** Two of them are CALCULATED and render
 * read-only (story 1.9 owns that guarantee; the schema only records which).
 */
import { z } from 'zod'

import { type CatalogoAberto, dataIso, dinheiro, id, valorDeCatalogo } from './comum'

/**
 * The statuses that exist today. **The union is not closed** (BR-24) — it is the
 * known initial set, useful for exhaustive `switch`es and for ordering the
 * lifecycle, and `statusOportunidadeSchema` deliberately accepts anything else.
 */
export const STATUS_OPORTUNIDADE_CONHECIDOS = [
  'rascunho',
  'nova',
  'proposta',
  'priorizada',
  'implementada',
  'arquivada',
] as const

export type StatusOportunidadeConhecido = (typeof STATUS_OPORTUNIDADE_CONHECIDOS)[number]

/** A status from the runtime catalog. Known values autocomplete; unknown ones parse. */
export type StatusOportunidade = CatalogoAberto<StatusOportunidadeConhecido>

export const statusOportunidadeSchema: z.ZodType<StatusOportunidade> = valorDeCatalogo

/** Narrowing helper — the only honest way to `switch` over an open catalog. */
export function ehStatusConhecido(status: string): status is StatusOportunidadeConhecido {
  return (STATUS_OPORTUNIDADE_CONHECIDOS as readonly string[]).includes(status)
}

export const oportunidadeSchema = z.object({
  idOportunidade: id,
  /**
   * Identificador LEGÍVEL (`OP-0041`), único por cliente. O `idOportunidade` é a
   * identidade; este é o rótulo que o arquiteto cita numa reunião ou num
   * relatório. Não é chave — nunca use para buscar.
   */
  codigo: z.string(),
  /** Implicit from the client selector (BR-19), but the server always echoes it back. */
  idCliente: id,

  /*
   * ⚠️ accountId, servicoAws, recurso e descricao são NULLABLE: em `rascunho` todo
   * campo é opcional (no banco também), e o gate do BR-20 os exige só na saída do
   * rascunho. Uma promoção do Cost Optimization Hub chega sem `recurso` — com o
   * campo obrigatório aqui, UM rascunho assim derrubou a grade inteira do IOX
   * (teste) em 15/10/2026. Não aperte de volta.
   */
  /** Dropdown — the client's registered accounts (BR-24). NEVER free text. */
  accountId: valorDeCatalogo.nullable(),
  /** Dropdown — global catalog (2p). NEVER free text. */
  servicoAws: valorDeCatalogo.nullable(),
  /** "o serviço é EC2, o recurso é a instância ABCDF" — free text, this one really is. */
  recurso: z.string().nullable(),
  descricao: z.string().nullable(),
  status: statusOportunidadeSchema,
  origem: z.enum(['automatico', 'manual']),
  /** The BR-01 gate, per opportunity. The Cliente's report is filtered SERVER-side. */
  visibilidadeCliente: z.boolean(),

  /** The FIRST/original launch date — the default period filter on 2e. */
  dataLancamento: dataIso,
  /** Required to reach `proposta` (BR-20). */
  dataProposta: dataIso.nullable(),
  /** Required to reach `implementada` (BR-20). */
  dataImplementacao: dataIso.nullable(),
  /**
   * Auto-filled (Compute Optimizer / Cost Optimization Hub / CUDOS / IA) or manual.
   * **`null` = the saving does not expire.** It bounds the *Resultado acumulado*
   * histogram (2h) — Eduardo: *"tem uma exceção que ela não vai ser para sempre
   * essa economia"*.
   */
  dataFimVigencia: dataIso.nullable(),

  /**
   * ⚠️ NULLABLE, e isso é o BR-20 e não descuido.
   *
   * Em `rascunho` **todo** campo é opcional — inclusive os custos. Uma
   * oportunidade promovida do Discovery nasce com conta, serviço, recurso e
   * descrição preenchidos pela ferramenta, e SEM os dois custos: a ferramenta dá
   * uma economia ESTIMADA, e derivar dois custos a partir dela seria inventar
   * número. O gate do BR-20 cobra os dois na saída de `rascunho`, que é
   * exatamente onde o arquiteto entra.
   *
   * (Declarados como não-nulos até 2026-08-20, o que tornava a fixture de
   * rascunho impossível de tipar — e teria feito a tela quebrar no primeiro
   * rascunho vindo da promoção.)
   */
  custoMensalAtual: dinheiro.nullable(),
  custoMensalPotencial: dinheiro.nullable(),
  /** CALCULATED = atual − potencial. Read-only in the UI. `null` propaga. */
  economiaMensalPotencial: dinheiro.nullable(),
  /** Input, on implementation. */
  custoMensalAposImplementacao: dinheiro.nullable(),
  /** CALCULATED. Read-only in the UI. */
  economiaMensalAposImplementacao: dinheiro.nullable(),

  /** USD hoje, travado no servidor. BR-03/BR-21 trazem BRL com PTAX manual. */
  moeda: z.string().default('USD'),

  /**
   * 🔴 BR-27. `ciclica` = Saving Plan ou Reserved Instance, cuja economia NÃO
   * acumula mês a mês como as demais. A regra exata de acumulação segue aberta
   * com a iOX; esta coluna CLASSIFICA, e a tela `2h` renderiza as duas séries
   * separadas em vez de somar errado com cara de certo.
   */
  tratamentoEconomia: z.enum(['acumulativa', 'ciclica']).default('acumulativa'),

  /** De qual linha do Discovery (`2d`) esta oportunidade nasceu. */
  idDiscovery: id.nullable().optional(),
})

export type Oportunidade = z.infer<typeof oportunidadeSchema>

/**
 * The two fields the form must render read-only. Exported so the screen cannot
 * disagree with the contract about which ones they are.
 */
export const CAMPOS_CALCULADOS = [
  'economiaMensalPotencial',
  'economiaMensalAposImplementacao',
] as const satisfies readonly (keyof Oportunidade)[]

/**
 * BR-20 field gates, mirrored from §7.1 so a blocked transition can say **which**
 * field is missing. **The server is authoritative** — this exists for the feel of
 * the form, never as the control.
 *
 * ⚠️ **`nova` is assumido — §9.** §7.1 states only *"all base fields required,
 * except those specific to `proposta`/`implementada`"*; **which fields count as
 * "base" is our reading, not iOX's.** `dataLancamento` is excluded because the
 * server sets it, and `visibilidadeCliente` because `false` is a valid answer
 * rather than a missing one — both are judgement calls.
 *
 * The other four rows are quoted directly from §7.1's gate table and are not
 * assumptions. Nothing consumes this yet; story **1.9** builds the form that does,
 * and should re-confirm `nova` against iOX before relying on it.
 */
export const CAMPOS_EXIGIDOS_POR_STATUS: Record<
  StatusOportunidadeConhecido,
  readonly (keyof Oportunidade)[]
> = {
  // Pre-filled from Discovery (2d) or half-typed by the architect: all optional.
  rascunho: [],
  // ⚠️ assumido — §9: see the note above. Complete and ready to present, but not
  // yet presented.
  nova: [
    'accountId',
    'servicoAws',
    'recurso',
    'descricao',
    'custoMensalAtual',
    'custoMensalPotencial',
  ],
  proposta: ['dataProposta'],
  priorizada: [],
  implementada: ['dataImplementacao', 'custoMensalAposImplementacao'],
  // Reachable from any status, at any time.
  arquivada: [],
}
